import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import { useTransContext } from '@mbarzda/solid-i18next'
import type { SectionsComparison } from '../../../features/engagement/domain/models/sections'
import { ChartContainerWithHeader, useChart } from '../../shared/charts'
import { getSectionsComparisonChartOptions } from './SectionsComparisonChart.vm'
import { SECTIONS_METRIC_OPTIONS, type SectionsMetricKey } from './SectionsStackedChart.vm'
import ToggleGroup from './ToggleGroup'

interface SectionsComparisonChartProps {
  comparison: SectionsComparison
}

const SectionsComparisonChart: Component<SectionsComparisonChartProps> = (props) => {
  let chartRef: HTMLDivElement | undefined

  const [t] = useTransContext()
  const [metric, setMetric] = createSignal<SectionsMetricKey>('seconds')

  useChart({
    data: () => ({ comparison: props.comparison, metric: metric() }),
    chartRef: () => chartRef,
    buildOptions: (data) => getSectionsComparisonChartOptions(data.comparison, data.metric, t)
  })

  // Any non-zero value across any metric means the chart has something to show:
  // a tier can have visits with no measured time (durations are web-only and
  // systematically missing on session-final views).
  const hasData = () =>
    props.comparison.segments.some((segment) =>
      segment.items.some(
        (item) =>
          item.section !== 'other' &&
          (item.secondsPerUser > 0 || item.visitsPerUser > 0 || item.interactionsPerUser > 0)
      )
    )

  return (
    <ChartContainerWithHeader
      hasData={hasData()}
      title={`${t('ls_sections_comparison_title')} ${t('ls_sections_excl_other')}`}
      headerRight={<ToggleGroup options={SECTIONS_METRIC_OPTIONS} value={metric()} onChange={setMetric} />}
    >
      <div ref={chartRef} />
    </ChartContainerWithHeader>
  )
}

export default SectionsComparisonChart
